import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';

import toast from '../../../../../utils/toast';
import { twoFactorAuthApiService } from '../../Services/API';
import { TwoFactorMethod } from '../../Services/Model';
import classes from './EditTwoFactorModal.module.css';
import { TotpEnrollmentSuccessModal } from './TotpEnrollmentSuccessModal';
import { TwoFactorLoadErrorModal } from './TwoFactorLoadErrorModal';

const TWO_FACTOR_DESCRIPTION_LOOKUP: Record<TwoFactorMethod, string> = {
  [TwoFactorMethod.SMS]: "SMS OTP",
  [TwoFactorMethod.AuthenticatorApp]: "Authenticator App OTP",
  [TwoFactorMethod.AuthenticatorAppPending]: "Authenticator App OTP (Pending)",
};

// Maps api response to internal enum
const TWO_FACTOR_METHOD_LOOKUP: Record<string, TwoFactorMethod> = {
  "sms": TwoFactorMethod.SMS,
  "phone": TwoFactorMethod.SMS,
  "totp": TwoFactorMethod.AuthenticatorApp,
  "totp_pending": TwoFactorMethod.AuthenticatorAppPending,
};

interface EditTwoFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

export const EditTwoFactorModal: React.FC<EditTwoFactorModalProps> = ({ isOpen, onClose, userId }) => {
  const [selectedOtpType, setSelectedOtpType] = useState<TwoFactorMethod>(TwoFactorMethod.SMS);
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentTwoFactorStatus, setCurrentTwoFactorStatus] = useState<TwoFactorMethod | null>(null);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [hasLoadError, setHasLoadError] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchTwoFactorStatus();
    }
  }, [isOpen, userId]);

  const fetchTwoFactorStatus = async () => {
    setIsLoading(true);
    setHasLoadError(false);
    try {
      const result = await twoFactorAuthApiService.get({ userId });
      const method = result?.method ?? TwoFactorMethod.AuthenticatorAppPending; // Null values occur when users are pending TOTP enrolment

      const twoFactorMethod =  method ? TWO_FACTOR_METHOD_LOOKUP[method] : TwoFactorMethod.AuthenticatorAppPending;

      if (!twoFactorMethod) {
        setHasLoadError(true);
        throw new Error(`Invalid 2FA method received: ${method}`);
      }
      
      setCurrentTwoFactorStatus(twoFactorMethod);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
      setHasLoadError(true);
      toast.error('Unable to fetch 2FA settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOtpType(event.target.value as TwoFactorMethod);
  };

  const handleActivate = async () => {
    setIsActivating(true);

    try {
      await twoFactorAuthApiService.update({ userId, method: selectedOtpType });

      if (selectedOtpType === TwoFactorMethod.SMS) {
        toast.success('Update successful!');
        onClose();
      } else {
        setEmailSent(true);
      }
    } catch (error) {
      toast.error('Failed to update 2FA settings');
    } finally {
      setIsActivating(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedOtpType(TwoFactorMethod.SMS);
    setIsActivating(false);
    setIsLoading(false);
    setCurrentTwoFactorStatus(null);
    setEmailSent(false);
    setHasLoadError(false);
  };

  const isSMSActive = currentTwoFactorStatus === TwoFactorMethod.SMS;
  const isSMSSelected = selectedOtpType === TwoFactorMethod.SMS;
  const isAuthReEnrolSelected = !isSMSActive && !isSMSSelected;
  const isActivateButtonDisabled = isActivating || isLoading || (isSMSActive && isSMSSelected) || hasLoadError;

  const buttonText = isAuthReEnrolSelected ? 'Re-Enrol' : 'Activate';
  const loadingText = isAuthReEnrolSelected ? 'Re-Enrolling...' : 'Activating...';
  const isFormDisabled = isLoading || isActivating || hasLoadError;

  if (emailSent) {
    return <TotpEnrollmentSuccessModal isOpen={isOpen} onClose={handleClose} />;
  }
  
  if (hasLoadError) {
    return <TwoFactorLoadErrorModal isOpen={isOpen} onClose={handleClose} />;
  }

  return (
    <Modal isOpen={isOpen} className={classes.modal} contentClassName={classes.modalContent}>
      <ModalHeader className={classes.modalHeader}>
        User OTP Login Options
      </ModalHeader>
      <ModalBody>
        <p className="mb-4">
          By default, the user should use SMS to receive their OTP. In some cases they will need to use the authenticator app.
        </p>

        {isLoading ? (
          <CurrentUserSettingsLoader />
        ) : (
          <>
            <div className='mb-4'>
              {currentTwoFactorStatus && (
                <>
                  Current setting:
                  <strong className="ml-1">{TWO_FACTOR_DESCRIPTION_LOOKUP[currentTwoFactorStatus]}</strong>
                </>
              )}
            </div>
            <Form className={classes.form}>
              <TwoFactorRadioOption
                method={TwoFactorMethod.SMS}
                selectedOtpType={selectedOtpType}
                onChange={handleOtpTypeChange}
                disabled={isFormDisabled}
                className="mr-2"
              />

              <TwoFactorRadioOption
                method={TwoFactorMethod.AuthenticatorApp}
                selectedOtpType={selectedOtpType}
                onChange={handleOtpTypeChange}
                disabled={isFormDisabled}
              />
            </Form>
          </>
        )}

      </ModalBody>

      <ModalFooter className={classes.modalFooter}>
        <Button
          color="primary"
          size="sm"
          onClick={handleActivate}
          disabled={isActivateButtonDisabled}
        >
          {isActivating ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" aria-hidden="true" />
              {loadingText}
            </>
          ) : (
            buttonText
          )}
        </Button>
        <Button
          color="secondary"
          size="sm"
          onClick={handleClose}
          disabled={isActivating}
        >
          Cancel
        </Button>
      </ModalFooter>

      {isAuthReEnrolSelected && (
        <div className="py-3">
          <p className={classes.warningText}>
            <strong>Note:</strong>'Re-Enrol' will resend an email to the user to setup Authenticator App OTP
          </p>
        </div>
      )}
    </Modal>
  );
};

const CurrentUserSettingsLoader: React.FC = () => (
    <div className="d-flex justify-content-center align-items-center py-4">
      <div className="spinner-border" aria-hidden="true" />
      <output className="ml-3">Loading current 2FA settings...</output>
    </div>
  );

interface TwoFactorRadioOptionProps {
  method: TwoFactorMethod;
  selectedOtpType: TwoFactorMethod;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  className?: string;
}

const TwoFactorRadioOption: React.FC<TwoFactorRadioOptionProps> = ({
  method,
  selectedOtpType,
  onChange,
  disabled,
  className = '',
}) => (
    <FormGroup check className={className}>
      <Label check className="d-flex align-items-center mb-0">
        <Input
          type="radio"
          name="otpType"
          value={method}
          checked={selectedOtpType === method}
          onChange={onChange}
          disabled={disabled}
          className="mr-2 mt-0"
        />
        <span>{TWO_FACTOR_DESCRIPTION_LOOKUP[method]}</span>
      </Label>
    </FormGroup>
  );